import React, { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from '../../utils/axiosConfig';
import { CheckIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';
import { useAuth } from '../../contexts/AuthContext';
import { useSelectedCourse } from '../../contexts/SelectedCourseContext';
import { formatCursoLabel, formatCursoNombreAnio } from '../../utils/cursoLabel';

interface Alumno {
  id: number;
  numeroAlumno: string;
  nombre: string;
  apellido: string;
  email: string;
}

interface Curso {
  id: number;
  nombre: string;
  codigo: string;
  anio?: number;
  tipoEvaluacion: 'nota_completa' | 'nota_por_tema';
  cantidadTemas: number;
  fechaEvaluacion: string;
  fechaRecuperatorio1: string;
  fechaRecuperatorio2: string;
  turnos: Turno[];
}

interface Turno {
  id: number;
  nombre: string;
  dia: string;
  horaInicio: string;
  horaFin: string;
  inscripciones: Inscripcion[];
}

interface Inscripcion {
  id: number;
  alumno: Alumno;
  estado: string;
}

interface Nota {
  id?: number;
  alumnoId: number;
  cursoId: number;
  turnoId: number;
  tipoEvaluacion: 'nota_completa' | 'nota_por_tema';
  notaCompleta?: string;
  notasPorTema?: string[];
  estado: 'pendiente' | 'cargada' | 'rectificada';
  observaciones?: string;
  fechaCarga?: string;
  cargadoPor?: {
    id: number;
    nombre: string;
    apellido: string;
    rol: string;
  };
  rectificadoPor?: {
    id: number;
    nombre: string;
    apellido: string;
    rol: string;
  };
}

// Interfaz para las notas con relaciones completas
interface NotaConRelaciones extends Nota {
  alumno: Alumno;
  curso: {
    id: number;
    nombre: string;
    codigo: string;
  };
  turno: {
    id: number;
    nombre: string;
  };
}

const PAGE_SIZE = 30;

const NotasPage: React.FC = () => {
  const { selectedCourse, courses, setSelectedCourse } = useSelectedCourse();
  const selectedCurso = selectedCourse;
  const [selectedTurno, setSelectedTurno] = useState<Turno | null>(null);
  const [selectedFechaExamenId, setSelectedFechaExamenId] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');

  // Limpiar datos al cambiar fecha de examen
  const handleFechaExamenChange = (fechaExamenId: string) => {
    const id = fechaExamenId ? parseInt(fechaExamenId, 10) : null;
    setSelectedFechaExamenId(id);
    // Limpiar datos de notas y observaciones al cambiar fecha
    setNotasData({});
    setObservacionesData({});
    setSearchTerm('');
  };
  const [showAllTurnos, setShowAllTurnos] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [notasData, setNotasData] = useState<{ [key: string]: string | number }>({});
  const [observacionesData, setObservacionesData] = useState<{ [key: string]: string }>({});
  const [mostrarNotasGuardadas, setMostrarNotasGuardadas] = useState<boolean>(false);
  const queryClient = useQueryClient();
  const { toast, showSuccess, showError, hideToast } = useToast();
  const { user } = useAuth();

  // Usar cursos del contexto
  const cursos = courses;

  // Fetch fechas de examen del curso
  interface FechaExamen {
    id: number;
    fecha: string;
    tipo: string;
    descripcion: string | null;
    fechaMuestra: string | null;
    activa: boolean;
  }

  const { data: fechasExamen, error: fechasError } = useQuery({
    queryKey: ['fechas-examen', selectedCurso?.id],
    queryFn: async (): Promise<FechaExamen[]> => {
      if (!selectedCurso) return [];
      try {
        const response = await axios.get(`/fechas-examen/curso/${selectedCurso.id}`);
        if (Array.isArray(response.data)) {
          return response.data;
        } else {
          console.error('Expected array but got:', typeof response.data);
          return [];
        }
      } catch (error) {
        console.error('Error fetching fechas de examen:', error);
        return [];
      }
    },
    enabled: !!selectedCurso,
    retry: 2,
    retryDelay: 1000,
  });

  // Fetch notas existentes por fecha (ya no se usa, las notas vienen en alumnosInscritos)
  const { data: notasExistentes, isLoading: loadingNotas, isFetching: fetchingNotas } = useQuery({
    queryKey: ['notas', selectedCurso?.id, selectedTurno?.id, showAllTurnos, selectedFechaExamenId],
    queryFn: async () => {
      // Ya no necesitamos obtener notas por separado, vienen en alumnosInscritos
      return [];
    },
    enabled: false // Deshabilitado porque las notas vienen en alumnosInscritos
  });

  // Fetch alumnos inscritos
  const { data: alumnosInscritos, isLoading: loadingAlumnos, isFetching: fetchingAlumnos, isError: errorAlumnos, error: errorAlumnosDetail } = useQuery({
    queryKey: ['alumnos-inscritos', selectedCurso?.id, selectedFechaExamenId, selectedTurno?.id, showAllTurnos],
    queryFn: async () => {
      if (!selectedCurso || !selectedFechaExamenId) return [];
      const params: any = { fechaExamenId: selectedFechaExamenId };
      if (!showAllTurnos && selectedTurno?.id) {
        params.turnoId = selectedTurno.id;
      }

      const response = await axios.get(`/notas/curso/${selectedCurso.id}/alumnos-inscritos`, {
        params,
        timeout: 60000,
      });
      const data = Array.isArray(response.data) ? response.data : [];
      console.log('Alumnos inscritos recibidos del backend:', data.length);
      return data;
    },
    enabled: !!selectedCurso && !!selectedFechaExamenId && (!!selectedTurno || showAllTurnos),
    retry: 1,
  });

  // Crear/actualizar nota
  const saveNotaMutation = useMutation({
    mutationFn: async (data: any) => {
      // Guardar alumnoId para usarlo en onSuccess, pero no enviarlo en el request
      const alumnoId = data.alumnoId;
      
      if (data.id) {
        // Actualización: no incluir cargadoPorId, alumnoId, cursoId, turnoId, solo enviar campos actualizables
        const { id, alumnoId: _, cursoId, turnoId, cargadoPorId, ...updateData } = data;
        const response = await axios.patch(`/notas/${id}`, updateData, {
          params: { usuarioId: user?.id }
        });
        // Agregar alumnoId a la respuesta para usarlo en onSuccess
        return { ...response.data, alumnoId };
      } else {
        // Creación: incluir todos los campos necesarios
        const notaData = {
          ...data,
          cargadoPorId: user?.id
        };
        const response = await axios.post(`/notas`, notaData, {
          params: { usuarioId: user?.id }
        });
        // Agregar alumnoId a la respuesta para usarlo en onSuccess
        return { ...response.data, alumnoId };
      }
    },
    onSuccess: (data: any, variables: any) => {
      // Actualizar solo la nota específica en el cache sin refetch completo
      // Actualizar la query de notas para incluir la nueva/actualizada nota
      queryClient.setQueryData(
        ['notas', selectedCurso?.id, selectedTurno?.id, showAllTurnos, selectedFechaExamenId],
        (oldData: any) => {
          if (!oldData) return oldData;
          const notaIndex = oldData.findIndex((n: any) => n.id === data.id);
          if (notaIndex >= 0) {
            // Actualizar nota existente
            const newData = [...oldData];
            newData[notaIndex] = data;
            return newData;
          } else {
            // Agregar nueva nota
            return [...oldData, data];
          }
        }
      );

      // Actualizar la query de alumnos-inscritos localmente sin refetch
      const alumnoId = data.alumnoId || variables.alumnoId;
      if (alumnoId && selectedCurso?.id && selectedFechaExamenId) {
        // Actualizar el cache localmente sin invalidar (sin refetch)
        queryClient.setQueryData(
          ['alumnos-inscritos', selectedCurso.id, selectedFechaExamenId, selectedTurno?.id, showAllTurnos],
          (oldData: any) => {
            if (!oldData) return oldData;
            return oldData.map((alumnoData: any) => {
              if (alumnoData.alumno.id === alumnoId) {
                return {
                  ...alumnoData,
                  notaFecha: {
                    ...data,
                    alumno: alumnoData.alumno,
                    curso: { id: selectedCurso.id, nombre: selectedCurso.nombre, codigo: selectedCurso.codigo, anio: selectedCurso.anio },
                    turno: alumnoData.turno
                  }
                };
              }
              return alumnoData;
            });
          }
        );

        // Actualizar notasData y observacionesData localmente con los valores guardados
        // Mantener los valores en los campos para que se vean actualizados
        if (selectedCurso.tipoEvaluacion === 'nota_por_tema' && data.notasPorTema) {
          const nuevasNotasData: { [key: string]: string | number } = { ...notasData };
          data.notasPorTema.forEach((notaTema: string, index: number) => {
            nuevasNotasData[`${alumnoId}_tema_${index}`] = notaTema || '';
          });
          setNotasData(nuevasNotasData);
        } else if (data.notaCompleta !== undefined) {
          setNotasData(prev => ({
            ...prev,
            [`${alumnoId}_completa`]: data.notaCompleta || ''
          }));
        }

        if (data.observaciones !== undefined) {
          setObservacionesData(prev => ({
            ...prev,
            [alumnoId.toString()]: data.observaciones || ''
          }));
        }
      }

      // Mostrar mensaje de éxito sin recargar
      showSuccess('Nota guardada', 'La nota se ha guardado correctamente');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || 'Error al guardar la nota';
      showError('Error al guardar', errorMessage);
    }
  });



  // Obtener nota existente para un alumno
  const getNotaExistente = (alumnoId: number) => {
    return notasExistentes?.find((nota: NotaConRelaciones) => nota.alumnoId === alumnoId);
  };

  // Cargar notas existentes en los campos cuando se detectan
  useEffect(() => {
    if (!alumnosInscritos || !selectedFechaExamenId) return;
    
    console.log('Cargando notas existentes...', { 
      alumnosInscritos: alumnosInscritos.length, 
      notasExistentes: notasExistentes?.length,
      selectedFechaExamenId 
    });
    
    const nuevasNotasData: { [key: string]: string | number } = {};
    const nuevasObservacionesData: { [key: string]: string } = {};
    
    alumnosInscritos.forEach((alumnoData: any) => {
      // Usar notaFecha que viene del backend (getAlumnosInscritos) o buscar en notasExistentes
      // Buscar la nota más reciente del alumno para esta fecha
      const notaExistente = alumnoData.notaFecha || (notasExistentes?.find((nota: NotaConRelaciones) => 
        nota.alumnoId === alumnoData.alumno.id
      ));
      
      if (notaExistente) {
        console.log(`Nota encontrada para alumno ${alumnoData.alumno.id} (${alumnoData.alumno.nombre} ${alumnoData.alumno.apellido}):`, {
          id: notaExistente.id,
          estado: notaExistente.estado,
          tipoEvaluacion: notaExistente.tipoEvaluacion,
          notaCompleta: notaExistente.notaCompleta,
          notasPorTema: notaExistente.notasPorTema,
          esArray: Array.isArray(notaExistente.notasPorTema),
          longitud: Array.isArray(notaExistente.notasPorTema) ? notaExistente.notasPorTema.length : 'N/A',
          fechaCarga: notaExistente.fechaCarga,
          vieneDeNotaFecha: !!alumnoData.notaFecha
        });
      } else {
        console.log(`NO se encontró nota para alumno ${alumnoData.alumno.id} (${alumnoData.alumno.nombre} ${alumnoData.alumno.apellido})`);
      }
      
      // Cargar la nota en los campos si existe (mostrar siempre, pero deshabilitar si está rectificada)
      if (notaExistente) {
        if (notaExistente.tipoEvaluacion === 'nota_completa') {
          if (notaExistente.notaCompleta) {
            nuevasNotasData[`${alumnoData.alumno.id}_completa`] = notaExistente.notaCompleta;
          }
        } else if (notaExistente.tipoEvaluacion === 'nota_por_tema') {
          // Cargar notas por tema, incluso si el array está vacío o tiene valores null/undefined
          if (Array.isArray(notaExistente.notasPorTema)) {
            notaExistente.notasPorTema.forEach((notaTema: string | null | undefined, index: number) => {
              // Convertir null/undefined a string vacío, pero mantener valores válidos
              const valor = notaTema != null && notaTema !== '' ? notaTema : '';
              nuevasNotasData[`${alumnoData.alumno.id}_tema_${index}`] = valor;
            });
          } else {
            // Si no hay array, inicializar con valores vacíos para mantener la estructura
            const cantidadTemas = selectedCurso?.cantidadTemas || 3;
            for (let i = 0; i < cantidadTemas; i++) {
              nuevasNotasData[`${alumnoData.alumno.id}_tema_${i}`] = '';
            }
          }
        }
        
        if (notaExistente.observaciones) {
          nuevasObservacionesData[alumnoData.alumno.id.toString()] = notaExistente.observaciones;
        }
      }
    });
    
    console.log('Datos cargados:', { nuevasNotasData, nuevasObservacionesData });
    
    // Reemplazar completamente los datos cuando se cargan las notas existentes
    setNotasData(nuevasNotasData);
    setObservacionesData(nuevasObservacionesData);
  }, [notasExistentes, alumnosInscritos, selectedFechaExamenId]);

  // Filtrar y ordenar alumnos alfabéticamente
  const alumnosFiltradosYOrdenados = useMemo(() => {
    if (!alumnosInscritos) return [];
    
    // Mostrar todos los alumnos (incluyendo los que tienen notas rectificadas)
    // Las notas rectificadas se mostrarán pero no se podrán editar
    let alumnosFiltrados = alumnosInscritos;
    
    // Filtrar por turno si no se está mostrando todos los turnos
    // (esto es un filtro adicional de seguridad, ya que el backend también filtra)
    if (!showAllTurnos && selectedTurno?.id) {
      alumnosFiltrados = alumnosFiltrados.filter((alumnoData: any) => {
        return alumnoData.turno?.id === selectedTurno.id;
      });
    }
    
    // Filtrar por término de búsqueda
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      alumnosFiltrados = alumnosFiltrados.filter((alumnoData: any) => {
        const nombre = `${alumnoData.alumno.nombre} ${alumnoData.alumno.apellido}`.toLowerCase();
        const numeroAlumno = alumnoData.alumno.numeroAlumno.toLowerCase();
        return nombre.includes(term) || numeroAlumno.includes(term);
      });
    }
    
    // Ordenar alfabéticamente por apellido y luego por nombre
    return alumnosFiltrados.sort((a: any, b: any) => {
      const apellidoA = a.alumno.apellido.toLowerCase();
      const apellidoB = b.alumno.apellido.toLowerCase();
      if (apellidoA !== apellidoB) {
        return apellidoA.localeCompare(apellidoB);
      }
      const nombreA = a.alumno.nombre.toLowerCase();
      const nombreB = b.alumno.nombre.toLowerCase();
      return nombreA.localeCompare(nombreB);
    });
  }, [alumnosInscritos, searchTerm, notasExistentes, selectedTurno, showAllTurnos]);

  const totalPages = Math.max(1, Math.ceil(alumnosFiltradosYOrdenados.length / PAGE_SIZE));

  const alumnosPaginados = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return alumnosFiltradosYOrdenados.slice(start, start + PAGE_SIZE);
  }, [alumnosFiltradosYOrdenados, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedTurno?.id, showAllTurnos, selectedFechaExamenId, selectedCurso?.id]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginaInicio = alumnosFiltradosYOrdenados.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const paginaFin = Math.min(currentPage * PAGE_SIZE, alumnosFiltradosYOrdenados.length);

  // Exportar PDF
  const handleExportPDF = async () => {
    if (!selectedCurso || !selectedFechaExamenId) {
      showError('Error', 'Debe seleccionar un curso y una fecha de examen');
      return;
    }

    // Obtener la fecha de examen seleccionada para el PDF
    const fechaExamenSeleccionada = fechasExamen?.find(fe => fe.id === selectedFechaExamenId);
    if (!fechaExamenSeleccionada) {
      showError('Error', 'No se encontró la fecha de examen seleccionada');
      return;
    }

    try {
      const fechaStr = fechaExamenSeleccionada.fecha.split('T')[0];
      const response = await axios.get(
        `/notas/curso/${selectedCurso.id}/pdf/${fechaStr}`,
        { responseType: 'blob' }
      );

      // Crear URL del blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      
      // Crear elemento de descarga
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${formatCursoNombreAnio(selectedCurso).replace(/\s+/g, '_')}-LISTADO-NOTAS-${fechaStr}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSuccess('PDF exportado', 'El archivo se ha descargado correctamente');
    } catch (error) {
      showError('Error al exportar', 'No se pudo generar el PDF');
    }
  };

  // Manejar cambio de nota
  const handleNotaChange = (alumnoId: number, temaIndex: number | null, value: string) => {
    const key = temaIndex !== null ? `${alumnoId}_tema_${temaIndex}` : `${alumnoId}_completa`;
    setNotasData(prev => ({ ...prev, [key]: value }));
  };

  // Manejar cambio de observaciones
  const handleObservacionChange = (alumnoId: number, value: string) => {
    setObservacionesData(prev => ({ ...prev, [alumnoId.toString()]: value }));
  };

  // Guardar nota
  const handleSaveNota = async (alumnoId: number) => {
    if (!selectedCurso) return;

    // Buscar la nota existente en alumnosInscritos (viene del backend)
    const alumnoData = alumnosInscritos?.find((a: any) => a.alumno.id === alumnoId);
    const notaExistente: NotaConRelaciones | undefined = alumnoData?.notaFecha || getNotaExistente(alumnoId);
    
    // Verificar si la nota está rectificada
    if (notaExistente && notaExistente.estado === 'rectificada') {
      showError('Error', 'No se puede modificar una nota que ya fue rectificada por un JTP');
      return;
    }
    
    // Si es ayudante, verificar que solo pueda modificar sus propias notas
    if (user?.rol === 'ayudante' && notaExistente && notaExistente.cargadoPor) {
      if (notaExistente.cargadoPor.id !== user.id) {
        showError('Error', 'No puedes modificar una nota que fue cargada por otro ayudante');
        return;
      }
    }
    
    const notaCompleta = notasData[`${alumnoId}_completa`];
    const notasPorTema = selectedCurso.tipoEvaluacion === 'nota_por_tema' 
      ? Array.from({ length: selectedCurso.cantidadTemas }, (_, i) => 
          notasData[`${alumnoId}_tema_${i}`] || ''
        )
      : undefined;

    // Obtener el turno del alumno
    // Primero intentar usar el turno que viene del backend en alumnoData
    let turnoId = alumnoData?.turno?.id;
    
    // Si no está disponible, usar el turno seleccionado
    if (!turnoId) {
      turnoId = selectedTurno?.id;
    }
    
    // Si estamos en "Todos los turnos" y aún no tenemos turno, buscar en los turnos del curso
    if (showAllTurnos && !turnoId) {
      const alumnoTurno = selectedCurso.turnos?.find(turno => 
        turno.inscripciones?.some((insc: any) => insc.alumno.id === alumnoId)
      );
      turnoId = alumnoTurno?.id;
    }

    if (!turnoId) {
      console.error('No se pudo determinar el turno del alumno', {
        alumnoId,
        alumnoData,
        selectedTurno,
        showAllTurnos,
        turnosCurso: selectedCurso.turnos
      });
      showError('Error de turno', 'No se pudo determinar el turno del alumno. Por favor, recarga la página e intenta nuevamente.');
      return;
    }

    // Preparar datos para enviar
    const notaData: any = {
      id: notaExistente?.id,
      tipoEvaluacion: selectedCurso.tipoEvaluacion
    };

    // Solo incluir observaciones si hay valor
    const observaciones = observacionesData[alumnoId.toString()];
    if (observaciones !== undefined) {
      notaData.observaciones = observaciones || '';
    }

    // Solo incluir los campos de notas según el tipo de evaluación
    if (selectedCurso.tipoEvaluacion === 'nota_completa') {
      if (notaCompleta !== undefined && notaCompleta !== '') {
        notaData.notaCompleta = notaCompleta;
      } else if (notaExistente?.id) {
        // Si es actualización y no hay nota, enviar null
        notaData.notaCompleta = null;
      }
    } else {
      if (notasPorTema && notasPorTema.length > 0) {
        notaData.notasPorTema = notasPorTema;
      } else if (notaExistente?.id) {
        // Si es actualización y no hay notas, enviar array vacío
        notaData.notasPorTema = [];
      }
    }

    // Si es una nueva nota, incluir los campos requeridos
    if (!notaExistente?.id) {
      notaData.alumnoId = alumnoId;
      notaData.cursoId = selectedCurso.id;
      notaData.turnoId = turnoId;
      notaData.fechaExamenId = selectedFechaExamenId; // Agregar fechaExamenId
      // Para nuevas notas, asegurar que los campos de notas estén presentes
      if (selectedCurso.tipoEvaluacion === 'nota_completa') {
        notaData.notaCompleta = notaCompleta || null;
      } else {
        notaData.notasPorTema = notasPorTema || [];
      }
    }

    // Incluir alumnoId en los datos para poder usarlo en onSuccess
    notaData.alumnoId = alumnoId;

    await saveNotaMutation.mutateAsync(notaData);
  };

  // Opciones de nota
  const opcionesNota = [
    { value: '', label: 'N/A' },
    { value: 'A', label: 'A - Aprobado' },
    { value: 'D', label: 'D - Desaprobado' },
    { value: 'DU', label: 'DU - Desaprobado por ausencia' },
    { value: 'B', label: 'B - Blanco' },
  ];

  // Renderizar campo de nota
  const renderNotaField = (alumnoId: number, temaIndex: number | null, valorInicial?: string | number, disabled?: boolean) => {
    const key = temaIndex !== null ? `${alumnoId}_tema_${temaIndex}` : `${alumnoId}_completa`;
    const valor = notasData[key] !== undefined ? notasData[key] : valorInicial || '';

    return (
      <select
        value={valor}
        onChange={(e) => handleNotaChange(alumnoId, temaIndex, e.target.value)}
        disabled={disabled}
        className={`w-20 px-2 py-1 text-center border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
      >
        {opcionesNota.map((opcion) => (
          <option key={opcion.value} value={opcion.value}>
            {opcion.value || opcion.label}
          </option>
        ))}
      </select>
    );
  };

  // Renderizar observaciones
  const renderObservacionesField = (alumnoId: number, valorInicial?: string, disabled?: boolean) => {
    const valor = observacionesData[alumnoId.toString()] !== undefined 
      ? observacionesData[alumnoId.toString()] 
      : valorInicial || '';

    return (
      <input
        type="text"
        value={valor}
        onChange={(e) => handleObservacionChange(alumnoId, e.target.value)}
        disabled={disabled}
        className={`w-full px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}`}
        placeholder="Observaciones..."
      />
    );
  };

  // Obtener color de nota
  const getNotaColor = (nota: string | number) => {
    if (!nota || nota === '') return 'text-gray-400';
    
    const notaStr = nota.toString().toUpperCase();
    if (notaStr === 'A') return 'text-green-600 font-bold';
    if (notaStr === 'D') return 'text-red-600 font-bold';
    if (notaStr === 'DU') return 'text-yellow-600 font-bold';
    if (notaStr === 'B') return 'text-gray-500 font-bold';
    
    const notaNum = parseFloat(notaStr);
    if (isNaN(notaNum)) return 'text-gray-600';
    if (notaNum >= 6) return 'text-green-600 font-bold';
    if (notaNum >= 4) return 'text-yellow-600 font-bold';
    return 'text-red-600 font-bold';
  };

  // Validar que hay un curso seleccionado (solo para docentes, no para admin)
  if (!selectedCurso && user?.rol !== 'admin') {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-yellow-800 mb-2">
            Selecciona un curso
          </h3>
          <p className="text-yellow-700">
            Por favor, selecciona un curso desde el selector superior para comenzar a cargar notas.
          </p>
        </div>
      </div>
    );
  }

  if (!Array.isArray(cursos) || cursos.length === 0) return <div className="text-center py-8 text-gray-600">No hay cursos disponibles</div>;

  return (
    <div className="space-y-3">
      {/* Barra superior compacta */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg px-4 py-2.5 text-white flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold leading-tight">Carga de Notas</h2>
          <p className="text-xs text-blue-100 hidden sm:block">A, D, DU, B — por tema o nota completa</p>
        </div>
        {selectedCurso && selectedFechaExamenId && (
          <button
            onClick={() => handleExportPDF()}
            className="shrink-0 bg-white/15 hover:bg-white/25 text-white text-xs font-medium px-3 py-1.5 rounded-md flex items-center gap-1.5 transition-colors"
          >
            <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Exportar PDF
          </button>
        )}
      </div>

      {/* Filtros + resumen del curso en una sola fila */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3">
        <div className={`grid grid-cols-1 gap-3 ${user?.rol !== 'admin' ? 'lg:grid-cols-4' : 'lg:grid-cols-3'}`}>
          {user?.rol !== 'admin' && (
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Curso</label>
              <select
                value={selectedCurso?.id || ''}
                onChange={(e) => {
                  const courseId = parseInt(e.target.value);
                  const course = cursos?.find((c: any) => c.id === courseId);
                  if (course) {
                    setSelectedCourse(course);
                  }
                  setSelectedTurno(null);
                  setShowAllTurnos(false);
                  setSelectedFechaExamenId(null);
                  setNotasData({});
                  setObservacionesData({});
                }}
                className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">Seleccionar curso</option>
                {Array.isArray(cursos) && cursos.map((curso: any) => (
                  <option key={curso.id} value={curso.id}>
                    {formatCursoLabel(curso)}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Turno</label>
            <select
              value={showAllTurnos ? 'all' : selectedTurno?.id || ''}
              onChange={(e) => {
                if (e.target.value === 'all') {
                  setShowAllTurnos(true);
                  setSelectedTurno(null);
                } else {
                  setShowAllTurnos(false);
                  const turno = selectedCurso && Array.isArray(selectedCurso.turnos) ? selectedCurso.turnos.find(t => t.id === parseInt(e.target.value)) : null;
                  setSelectedTurno(turno || null);
                }
                setSelectedFechaExamenId(null);
                setNotasData({});
                setObservacionesData({});
                setSearchTerm('');
              }}
              disabled={!selectedCurso}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            >
              <option value="">Seleccionar turno</option>
              <option value="all">Todos los turnos</option>
              {selectedCurso?.turnos && Array.isArray(selectedCurso.turnos) && selectedCurso.turnos.map((turno) => (
                <option key={turno.id} value={turno.id}>
                  {turno.nombre} ({turno.dia} {turno.horaInicio}-{turno.horaFin})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Fecha de examen</label>
            <select
              value={selectedFechaExamenId || ''}
              onChange={(e) => handleFechaExamenChange(e.target.value)}
              disabled={!selectedTurno && !showAllTurnos}
              className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            >
              <option value="">Seleccionar fecha</option>
              {Array.isArray(fechasExamen) && fechasExamen.map((fechaExamen: FechaExamen) => {
                const fechaStr = fechaExamen.fecha.split('T')[0];
                const tipoStr = fechaExamen.tipo === 'primera_fecha' ? '1ra Fecha' :
                               fechaExamen.tipo === 'recuperatorio_1' ? 'Recup. 1' :
                               fechaExamen.tipo === 'recuperatorio_2' ? 'Recup. 2' :
                               fechaExamen.tipo === 'fecha_extra' ? 'Extra' : fechaExamen.tipo;
                const label = fechaExamen.descripcion
                  ? `${fechaStr} — ${tipoStr} (${fechaExamen.descripcion})`
                  : `${fechaStr} — ${tipoStr}`;
                return (
                  <option key={fechaExamen.id} value={fechaExamen.id}>
                    {label}
                  </option>
                );
              })}
            </select>
          </div>
          {selectedCurso && (
            <div className="flex flex-wrap items-end gap-2 lg:justify-end">
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                {selectedCurso.tipoEvaluacion === 'nota_completa' ? 'Nota completa' : `${selectedCurso.cantidadTemas} temas`}
              </span>
              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200">
                {alumnosFiltradosYOrdenados.length > 0
                  ? `${alumnosFiltradosYOrdenados.length} alumnos`
                  : showAllTurnos
                    ? `${selectedCurso?.turnos?.reduce((total, turno) => total + (turno.inscripciones?.length || 0), 0) || 0} inscriptos`
                    : `${selectedTurno?.inscripciones?.length || 0} inscriptos`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Lista de alumnos con notas */}
      {((selectedTurno && selectedFechaExamenId) || (showAllTurnos && selectedFechaExamenId)) && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-2 min-w-0">
              <h3 className="text-sm font-semibold text-gray-900 truncate">
                {selectedTurno?.nombre || 'Todos los turnos'}
              </h3>
              {selectedCurso && (
                <span className="text-xs text-gray-500">
                        {formatCursoLabel(selectedCurso)}
                </span>
              )}
            </div>
            <div className="relative w-full sm:w-56">
              <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar alumno..."
                className="block w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          {(searchTerm || alumnosFiltradosYOrdenados.length > 0) && (
            <div className="px-4 py-1.5 border-b border-gray-100 bg-white text-xs text-gray-500">
              {searchTerm && (
                <span>{alumnosFiltradosYOrdenados.length} resultado{alumnosFiltradosYOrdenados.length !== 1 ? 's' : ''} · </span>
              )}
              {alumnosFiltradosYOrdenados.length > 0 && (
                <span>
                  Mostrando {paginaInicio}–{paginaFin} de {alumnosFiltradosYOrdenados.length} alumnos
                </span>
              )}
            </div>
          )}

          {(loadingAlumnos || fetchingAlumnos || loadingNotas || fetchingNotas) ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-4 text-gray-600">
                  {showAllTurnos ? 'Cargando alumnos de todos los turnos...' : 'Cargando alumnos y notas...'}
                </p>
              </div>
            </div>
          ) : errorAlumnos ? (
            <div className="flex items-center justify-center py-12 px-4">
              <div className="text-center max-w-md">
                <p className="text-red-600 font-medium">Error al cargar alumnos</p>
                <p className="text-sm text-gray-500 mt-2">
                  {(errorAlumnosDetail as any)?.code === 'ECONNABORTED'
                    ? 'La consulta tardó demasiado. Probá de nuevo o seleccioná un turno específico.'
                    : (errorAlumnosDetail as any)?.response?.data?.message || 'No se pudo obtener el listado. Reintentá en unos segundos.'}
                </p>
              </div>
            </div>
          ) : (
          <div className="overflow-x-auto max-h-[calc(100vh-220px)] overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-purple-50 sticky top-0 z-10">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    Alumno
                  </th>
                  {selectedCurso?.tipoEvaluacion === 'nota_por_tema' ? (
                    Array.from({ length: selectedCurso.cantidadTemas }, (_, i) => (
                      <th key={i} className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                        Tema {i + 1}
                      </th>
                    ))
                  ) : (
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                      Nota
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    Observaciones
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-700 uppercase tracking-wider border-b border-gray-200">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {alumnosFiltradosYOrdenados.length === 0 ? (
                  <tr>
                    <td colSpan={selectedCurso?.tipoEvaluacion === 'nota_por_tema' ? selectedCurso.cantidadTemas + 3 : 4} className="px-4 py-8 text-center text-gray-500">
                      {searchTerm
                        ? 'No se encontraron alumnos con ese criterio de búsqueda'
                        : alumnosInscritos && alumnosInscritos.length === 0
                          ? 'No hay alumnos pendientes para esta fecha (pueden estar todos aprobados en fechas anteriores)'
                          : 'No hay alumnos inscritos'}
                    </td>
                  </tr>
                ) : (
                  alumnosPaginados.map((alumnoData: any) => {
                  const notaExistente = alumnoData.notaFecha;
                  const puedeCargarNota = alumnoData.puedeCargarNota;
                  
                  // Verificar si un ayudante puede modificar esta nota
                  const puedeModificarNota = user?.rol === 'ayudante' 
                    ? (!notaExistente || !notaExistente.cargadoPor || notaExistente.cargadoPor.id === user.id)
                    : true;
                  
                  return (
                    <tr key={`${alumnoData.alumno.id}-${alumnoData.turno?.id ?? 'sin-turno'}`} className={`transition-colors ${!puedeCargarNota ? 'bg-gray-50' : 'hover:bg-blue-50'} ${notaExistente?.estado === 'rectificada' ? 'bg-green-50' : ''}`}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-3">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white font-semibold text-sm">
                              {alumnoData.alumno.nombre.charAt(0)}{alumnoData.alumno.apellido.charAt(0)}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900 truncate">
                              {alumnoData.alumno.nombre} {alumnoData.alumno.apellido}
                            </div>
                            <div className="text-xs text-gray-500 mt-0.5">
                              N° {alumnoData.alumno.numeroAlumno}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {alumnoData.turno?.nombre || 'Sin turno'}
                              </span>
                              {!puedeCargarNota && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  Aprobado
                                </span>
                              )}
                              {!puedeModificarNota && notaExistente?.cargadoPor && user?.rol === 'ayudante' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800">
                                  Otro ayudante
                                </span>
                              )}
                              {notaExistente?.estado === 'rectificada' && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                  Rectificada
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>

                      {selectedCurso?.tipoEvaluacion === 'nota_por_tema' ? (
                        Array.from({ length: selectedCurso.cantidadTemas }, (_, i) => {
                          // Obtener el valor de la nota desde notasData o desde notaExistente
                          const valorNota = notasData[`${alumnoData.alumno.id}_tema_${i}`] !== undefined
                            ? notasData[`${alumnoData.alumno.id}_tema_${i}`]
                            : (notaExistente?.notasPorTema?.[i] || '');
                          const estaRectificada = notaExistente?.estado === 'rectificada';
                          // Solo deshabilitar si está rectificada o si no puede cargar nota (aprobado anteriormente)
                          // Si está cargada pero no rectificada, permitir edición
                          const mostrarSelect = !estaRectificada && puedeCargarNota;
                          
                          return (
                            <td key={i} className="px-4 py-3 whitespace-nowrap text-center">
                              {mostrarSelect ? (
                                <div className="flex justify-center">
                                  {renderNotaField(
                                    alumnoData.alumno.id, 
                                    i, 
                                    valorNota,
                                    false // No deshabilitar el select
                                  )}
                                </div>
                              ) : (
                                <div className="flex flex-col items-center">
                                  <span className={`inline-flex items-center justify-center w-12 h-8 rounded-md font-semibold text-sm ${getNotaColor(valorNota)}`}>
                                    {valorNota || '-'}
                                  </span>
                                </div>
                              )}
                            </td>
                          );
                        })
                      ) : (
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          {(() => {
                            // Obtener el valor de la nota desde notasData o desde notaExistente
                            const valorNota = notasData[`${alumnoData.alumno.id}_completa`] !== undefined
                              ? notasData[`${alumnoData.alumno.id}_completa`]
                              : (notaExistente?.notaCompleta || '');
                            const estaRectificada = notaExistente?.estado === 'rectificada';
                            // Solo deshabilitar si está rectificada o si no puede cargar nota (aprobado anteriormente)
                            // Si está cargada pero no rectificada, permitir edición
                            const mostrarSelect = !estaRectificada && puedeCargarNota;
                            
                            return mostrarSelect ? (
                              <div className="flex justify-center">
                                {renderNotaField(
                                  alumnoData.alumno.id, 
                                  null, 
                                  valorNota,
                                  false // No deshabilitar el select
                                )}
                              </div>
                            ) : (
                              <div className="flex flex-col items-center">
                                <span className={`inline-flex items-center justify-center w-12 h-8 rounded-md font-semibold text-sm ${getNotaColor(valorNota)}`}>
                                  {valorNota || '-'}
                                </span>
                              </div>
                            );
                          })()}
                        </td>
                      )}

                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="max-w-xs">
                          {(() => {
                            const valorObservaciones = observacionesData[alumnoData.alumno.id.toString()] !== undefined
                              ? observacionesData[alumnoData.alumno.id.toString()]
                              : (notaExistente?.observaciones || '');
                            const estaRectificada = notaExistente?.estado === 'rectificada';
                            // Solo deshabilitar si está rectificada o si no puede cargar nota
                            const mostrarInput = !estaRectificada && puedeCargarNota;
                            
                            return mostrarInput ? (
                              renderObservacionesField(
                                alumnoData.alumno.id, 
                                valorObservaciones,
                                false // No deshabilitar el input
                              )
                            ) : (
                              <span className="text-gray-600 text-sm">
                                {valorObservaciones || '-'}
                              </span>
                            );
                          })()}
                        </div>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        {(() => {
                          const estaRectificada = notaExistente?.estado === 'rectificada';
                          // Permitir guardar si no está rectificada y puede cargar nota
                          // Si es ayudante, también verificar que pueda modificar
                          const puedeGuardar = puedeCargarNota && !estaRectificada && 
                            (user?.rol !== 'ayudante' || !notaExistente?.cargadoPor || notaExistente.cargadoPor.id === user.id);
                          
                          if (puedeGuardar) {
                            return (
                              <button
                                onClick={() => handleSaveNota(alumnoData.alumno.id)}
                                disabled={saveNotaMutation.isPending}
                                className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                              >
                                <CheckIcon className="h-4 w-4 mr-1" />
                                {saveNotaMutation.isPending ? 'Guardando...' : 'Guardar'}
                              </button>
                            );
                          } else if (estaRectificada) {
                            return (
                              <span className="text-blue-600 text-sm font-medium">
                                Rectificada
                              </span>
                            );
                          } else if (!puedeCargarNota) {
                            return (
                              <span className="text-red-600 text-sm">
                                Aprobado
                              </span>
                            );
                          } else {
                            return (
                              <span className="text-gray-400 text-sm">
                                No disponible
                              </span>
                            );
                          }
                        })()}
                      </td>
                    </tr>
                  );
                  })
                )}
              </tbody>
            </table>
          </div>
          )}

          {alumnosFiltradosYOrdenados.length > PAGE_SIZE && (
            <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-center justify-between gap-3">
              <p className="text-sm text-gray-600">
                Página {currentPage} de {totalPages}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Primera
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Anterior
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Siguiente
                </button>
                <button
                  type="button"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Última
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Notas guardadas recientemente - Solo mostrar si está habilitado */}
      {notasExistentes && notasExistentes.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 bg-green-50 flex justify-between items-center">
            <h3 className="text-lg font-medium text-green-900">
              Notas Guardadas ({notasExistentes.length})
            </h3>
            <button
              onClick={() => setMostrarNotasGuardadas(!mostrarNotasGuardadas)}
              className="text-sm text-green-700 hover:text-green-900 font-medium"
            >
              {mostrarNotasGuardadas ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
          
          {mostrarNotasGuardadas && (

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Alumno
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nota
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Observaciones
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Estado
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Array.isArray(notasExistentes) && notasExistentes.map((nota: NotaConRelaciones) => (
                  <tr key={nota.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {nota.alumno.nombre} {nota.alumno.apellido}
                      </div>
                      <div className="text-xs text-gray-500">
                        N° {nota.alumno.numeroAlumno}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {nota.tipoEvaluacion === 'nota_completa' ? (
                        <span className={getNotaColor(nota.notaCompleta || '')}>
                          {nota.notaCompleta || 'N/A'}
                        </span>
                      ) : (
                        <div className="flex space-x-1 justify-center">
                          {Array.isArray(nota.notasPorTema) && nota.notasPorTema.map((notaTema, index) => (
                            <span key={index} className={`px-2 py-1 bg-gray-100 rounded text-xs ${getNotaColor(notaTema)}`}>
                              T{index + 1}: {notaTema || 'N/A'}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {nota.observaciones || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex flex-col items-center gap-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          nota.estado === 'cargada' 
                            ? 'bg-green-100 text-green-800' 
                            : nota.estado === 'rectificada'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {nota.estado === 'cargada' ? 'Cargada' : 
                           nota.estado === 'rectificada' ? 'Rectificada' : 'Pendiente'}
                        </span>
                        {nota.cargadoPor && (
                          <span className="text-xs text-gray-500">
                            Por: {nota.cargadoPor.nombre} {nota.cargadoPor.apellido}
                          </span>
                        )}
                        {nota.rectificadoPor && (
                          <span className="text-xs text-blue-600">
                            Rectificada por: {nota.rectificadoPor.nombre} {nota.rectificadoPor.apellido}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      )}
      
      {/* Toast notifications */}
      <Toast
        type={toast.type}
        title={toast.title}
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
      />
    </div>
  );
};

export default NotasPage;
